import type { DateTime } from 'luxon';
import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../../content/hooks';
import type { ClientModel, Order, VisitStatus, VisitsClientReportData, VisitsClientSortingfields } from '../../models';

interface IReportsContext {
  loading: boolean;
  setLoading: (val: boolean) => void;
  dataUpdate: boolean;
  toggleUpdate: () => void;
  data: VisitsClientReportData[];
  setData: (val: VisitsClientReportData[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: VisitsClientSortingfields;
  setOrderBy: (val: VisitsClientSortingfields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: VisitsClientSortingfields) => void;
  count: number;
  setCount: (val: number) => void;
  fromDate: DateTime | null;
  setFromDate: (val: DateTime | null) => void;
  toDate: DateTime | null;
  setToDate: (val: DateTime | null) => void;
  client: ClientModel | null;
  setClient: (val: ClientModel | null) => void;
  status: VisitStatus | null;
  setStatus: (val: VisitStatus | null) => void;
}

export const VisitsClientContext = createContext<IReportsContext>({} as IReportsContext);

export const VisitsClientProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [dataUpdate, setDataUpdate] = useState(false);
  const toggleUpdate = (): void => setDataUpdate(!dataUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<VisitsClientSortingfields>(toggleUpdate, 'date', 'desc');

  const [data, setData] = useState<VisitsClientReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<DateTime | null>(null);
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [client, setClient] = useState<ClientModel | null>(null);
  const [status, setStatus] = useState<VisitStatus | null>(null);

  return (
    <VisitsClientContext.Provider
      value={{
        data,
        setData,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        dataUpdate,
        toggleUpdate,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        client,
        setClient,
        status,
        setStatus
      }}
    >
      {children}
    </VisitsClientContext.Provider>
  );
};
